import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { api } from '@/lib/axios';
import {
  SuperLampParams,
  MacroProfile,
  SurfaceTexture,
  SocketStandard,
  generateSuperLampGeometry,
  exportSuperLampToSTL,
  getSocketHoleDiameter
} from '@/lib/lampGenerator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  Bookmark,
  RotateCcw,
  Box,
  Layers,
  ArrowLeft,
  Lightbulb,
  Grid3X3,
  Sliders,
  Printer,
  Shapes,
  Maximize2,
  RotateCw,
  Crosshair
} from 'lucide-react';
import { toast } from 'sonner';

// Colección de presets emblemáticos Mila Design
interface PresetItem {
  name: string;
  badge: string;
  description: string;
  params: SuperLampParams;
}

const SIGNATURE_PRESETS: PresetItem[] = [
  {
    name: 'Table Lamp 2.0',
    badge: 'Mila Signature',
    description: '3 Ondas fluidas con micro-puntos moleteados tipo knurling',
    params: {
      profile: 'ripple',
      height: 180,
      bottomDiameter: 130,
      topDiameter: 85,
      verticalSections: 3,
      macroBulgeAmplitude: 14,
      polygonSides: 64,
      twistAngle: 0,
      isLowPoly: false,
      staggerVertices: false,
      texture: 'knurling',
      textureScaleX: 32,
      textureScaleY: 20,
      textureDepth: 3.5,
      printMode: 'vase',
      wallThickness: 1.6,
      hasSocketHole: true,
      socketType: 'e27',
      customSocketDiameter: 41.5,
      socketLipHeight: 0
    }
  },
  {
    name: 'Diamond Big',
    badge: 'Malla Rombos',
    description: 'Silueta abultada con matriz de rombos salientes en relieve',
    params: {
      profile: 'donut',
      height: 160,
      bottomDiameter: 120,
      topDiameter: 90,
      verticalSections: 1,
      macroBulgeAmplitude: 12,
      polygonSides: 56,
      twistAngle: 0,
      isLowPoly: false,
      staggerVertices: false,
      texture: 'diamond',
      textureScaleX: 18,
      textureScaleY: 12,
      textureDepth: 4.5,
      printMode: 'vase',
      wallThickness: 1.6,
      hasSocketHole: true,
      socketType: 'e27',
      customSocketDiameter: 41.5,
      socketLipHeight: 0
    }
  },
  {
    name: 'Triangle Bottle',
    badge: 'Triángulos',
    description: 'Cuello nórdico con patrón de triángulos desplazados',
    params: {
      profile: 'bottle',
      height: 190,
      bottomDiameter: 140,
      topDiameter: 70,
      verticalSections: 2,
      macroBulgeAmplitude: 8,
      polygonSides: 48,
      twistAngle: 15,
      isLowPoly: false,
      staggerVertices: false,
      texture: 'triangles',
      textureScaleX: 20,
      textureScaleY: 14,
      textureDepth: 4.0,
      printMode: 'vase',
      wallThickness: 1.6,
      hasSocketHole: true,
      socketType: 'e27',
      customSocketDiameter: 41.5,
      socketLipHeight: 0
    }
  },
  {
    name: 'Low Poly Nórdica',
    badge: 'Flat Shading',
    description: 'Facetado octogonal con desfase triangular romboidal afilado',
    params: {
      profile: 'accordion',
      height: 170,
      bottomDiameter: 130,
      topDiameter: 80,
      verticalSections: 4,
      macroBulgeAmplitude: 16,
      polygonSides: 8,
      twistAngle: 30,
      isLowPoly: true,
      staggerVertices: true,
      texture: 'none',
      textureScaleX: 1,
      textureScaleY: 1,
      textureDepth: 0,
      printMode: 'vase',
      wallThickness: 1.6,
      hasSocketHole: true,
      socketType: 'e27',
      customSocketDiameter: 41.5,
      socketLipHeight: 0
    }
  },
  {
    name: 'Twist Helical',
    badge: 'Helicoidal',
    description: 'Espirales continuas dinámicas con torsión en altura',
    params: {
      profile: 'linear',
      height: 175,
      bottomDiameter: 125,
      topDiameter: 85,
      verticalSections: 1,
      macroBulgeAmplitude: 0,
      polygonSides: 48,
      twistAngle: 90,
      isLowPoly: false,
      staggerVertices: false,
      texture: 'spiral_ribs',
      textureScaleX: 14,
      textureScaleY: 3,
      textureDepth: 5.5,
      printMode: 'vase',
      wallThickness: 1.6,
      hasSocketHole: true,
      socketType: 'e27',
      customSocketDiameter: 41.5,
      socketLipHeight: 0
    }
  },
  {
    name: 'Japandi Fluted',
    badge: 'Minimalista',
    description: 'Cintura cóncava sutil con acanalado fino vertical',
    params: {
      profile: 'concave',
      height: 165,
      bottomDiameter: 120,
      topDiameter: 95,
      verticalSections: 1,
      macroBulgeAmplitude: 8,
      polygonSides: 44,
      twistAngle: 0,
      isLowPoly: false,
      staggerVertices: false,
      texture: 'knurling',
      textureScaleX: 36,
      textureScaleY: 2,
      textureDepth: 2.2,
      printMode: 'vase',
      wallThickness: 1.6,
      hasSocketHole: true,
      socketType: 'e27',
      customSocketDiameter: 41.5,
      socketLipHeight: 0
    }
  }
];

const defaultParams: SuperLampParams = SIGNATURE_PRESETS[0].params;

export default function GeneratorPage() {
  const mountRef = useRef<HTMLDivElement>(null);
  const [params, setParams] = useState<SuperLampParams>(defaultParams);
  const [wireframe, setWireframe] = useState(false);
  const [color, setColor] = useState('#D8C7B5'); // Arena / Lino
  const [lightOn, setLightOn] = useState(false); // Simulación de bombilla interior cálida
  const [autoRotate, setAutoRotate] = useState(false); // Giro automático suave
  const [activeTab, setActiveTab] = useState<'macro' | 'texture' | 'lowpoly' | 'print'>('macro');
  const [presets, setPresets] = useState<any[]>([]);
  const [presetName, setPresetName] = useState('');

  // Three.js refs
  const sceneRef = useRef<THREE.Scene | null>(null);
  const meshRef = useRef<THREE.Mesh | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const geomRef = useRef<THREE.BufferGeometry | null>(null);
  const bulbLightRef = useRef<THREE.PointLight | null>(null);
  const gridRef = useRef<THREE.GridHelper | null>(null);

  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.autoRotate = autoRotate;
    }
  }, [autoRotate]);

  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      const res = await api.get('/generator-presets');
      setPresets(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setPresets([]);
    }
  };

  // 1. Inicialización de Three.js Canvas con limpieza estricta
  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    const width = container.clientWidth;
    const height = container.clientHeight || 560;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xF9F8F6);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, width / height, 1, 1000);
    camera.position.set(0, 150, 320);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    rendererRef.current = renderer;

    container.appendChild(renderer.domElement);

    // Iluminación de estudio
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 0.85);
    keyLight.position.set(160, 220, 160);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xE89C6F, 0.4);
    fillLight.position.set(-160, 120, -120);
    scene.add(fillLight);

    // Bombilla interna para simulación cálida (inicialmente apagada)
    const bulbLight = new THREE.PointLight(0xffaa5e, 0, 350, 1.2);
    bulbLight.position.set(0, 70, 0);
    scene.add(bulbLight);
    bulbLightRef.current = bulbLight;

    // Grilla Japandi
    const grid = new THREE.GridHelper(320, 32, 0xD4CDC5, 0xEAE4DD);
    grid.position.y = -0.5;
    scene.add(grid);
    gridRef.current = grid;

    // Malla inicial
    const geom = generateSuperLampGeometry(params);
    geomRef.current = geom;

    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(color),
      roughness: 0.4,
      metalness: 0.05,
      side: THREE.DoubleSide,
      wireframe
    });

    const mesh = new THREE.Mesh(geom, material);
    meshRef.current = mesh;
    scene.add(mesh);

    // Controles orbitales de cámara (rotan el modelo y la base juntos con amortiguación suave)
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, params.height * 0.45, 0);
    controls.autoRotate = autoRotate;
    controls.autoRotateSpeed = 2.0;
    controls.maxDistance = 650;
    controls.minDistance = 60;
    controlsRef.current = controls;

    // Render loop
    let animId: number;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!container || !rendererRef.current) return;
      const newW = container.clientWidth;
      const newH = container.clientHeight || 560;
      camera.aspect = newW / newH;
      camera.updateProjectionMatrix();
      rendererRef.current.setSize(newW, newH);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
      controls.dispose();
      renderer.dispose();
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, []);

  // 2. Actualizar geometría cuando cambian los parámetros paramétricos
  useEffect(() => {
    if (!meshRef.current || !sceneRef.current) return;

    const newGeom = generateSuperLampGeometry(params);
    geomRef.current = newGeom;

    meshRef.current.geometry.dispose();
    meshRef.current.geometry = newGeom;

    // Actualizar posición de la bombilla interior a la altura media
    if (bulbLightRef.current) {
      bulbLightRef.current.position.y = params.height * 0.45;
    }

    // Mantener el centro de rotación alineado con la altura de la lámpara
    if (controlsRef.current) {
      controlsRef.current.target.set(0, params.height * 0.45, 0);
    }
  }, [params]);

  const handleRecenter = () => {
    if (!cameraRef.current || !controlsRef.current) return;
    const h = params.height;
    controlsRef.current.target.set(0, h * 0.45, 0);
    cameraRef.current.position.set(0, h * 0.85, Math.max(200, h * 1.8));
    controlsRef.current.update();
    toast.info('Vista centrada');
  };

  // 3. Actualizar material y simulación de luz
  useEffect(() => {
    if (!meshRef.current || !sceneRef.current) return;
    const mat = meshRef.current.material as THREE.MeshStandardMaterial;
    mat.wireframe = wireframe;

    if (lightOn) {
      // Modo noche con bombilla cálida 2700K encendida
      sceneRef.current.background = new THREE.Color(0x181716);
      if (gridRef.current) (gridRef.current.material as any).opacity = 0.15;
      if (bulbLightRef.current) bulbLightRef.current.intensity = 4.2;

      mat.color.set(color);
      mat.emissive.set(0x45220c); // Resplandor cálido interior
      mat.roughness = 0.55;
    } else {
      // Modo estudio de día
      sceneRef.current.background = new THREE.Color(0xF9F8F6);
      if (gridRef.current) (gridRef.current.material as any).opacity = 1.0;
      if (bulbLightRef.current) bulbLightRef.current.intensity = 0;

      mat.color.set(color);
      mat.emissive.set(0x000000);
      mat.roughness = 0.4;
    }
  }, [color, wireframe, lightOn]);

  const handleApplyPreset = (item: PresetItem) => {
    setParams(item.params);
    toast.success(`Preset "${item.name}" aplicado`);
  };

  const handleExportSTL = () => {
    if (!geomRef.current) return;
    const modeLabel = params.printMode === 'vase' ? 'modo-vaso' : `solido-${params.wallThickness}mm`;
    const socketLabel = params.hasSocketHole ? params.socketType : 'sin-base';
    const filename = `pantalla-${params.profile}-${params.texture}-${modeLabel}-${socketLabel}.stl`;
    toast.success('Generando archivo binario STL calibrado...');
    exportSuperLampToSTL(geomRef.current, filename);
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Ingresá un nombre para el preset');
      return;
    }
    try {
      await api.post('/generator-presets', {
        name: presetName,
        type: params.profile,
        params: JSON.stringify(params)
      });
      toast.success('¡Preset guardado en biblioteca!');
      setPresetName('');
      loadPresets();
    } catch {
      toast.error('Error al guardar preset');
    }
  };

  const handleLoadUserPreset = (p: any) => {
    try {
      const parsed = typeof p.params === 'string' ? JSON.parse(p.params) : p.params;
      setParams(parsed);
      toast.success(`Preset "${p.name}" aplicado`);
    } catch {
      toast.error('No se pudo cargar el preset');
    }
  };

  const currentSocketDiam = getSocketHoleDiameter(params.socketType, params.customSocketDiameter);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Cabecera */}
      <div className="space-y-3">
        <Link
          to="/tools"
          className="inline-flex items-center text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Volver al Hub de Herramientas
        </Link>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Super Generador Paramétrico 3D</h1>
              <Badge className="bg-primary/10 text-primary border-primary/20">Mila Signature</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Desacoplá la silueta macro de la micro-textura volumétrica, configurá el zócalo para portalámpara y exportá en STL para Bambu Studio / OrcaSlicer.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setParams(defaultParams)}
              className="gap-1.5"
            >
              <RotateCcw className="h-4 w-4" /> Reset
            </Button>
            <Button
              size="sm"
              onClick={handleExportSTL}
              className="gap-1.5 bg-primary text-primary-foreground shadow-sm"
            >
              <Download className="h-4 w-4" /> Exportar STL
            </Button>
          </div>
        </div>
      </div>

      {/* Selector de Presets Emblemáticos Mila Design */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Colección Mila Design
          </Label>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {SIGNATURE_PRESETS.map(item => {
            const isMatch = params.profile === item.params.profile && params.texture === item.params.texture;
            return (
              <button
                key={item.name}
                onClick={() => handleApplyPreset(item)}
                className={`p-2.5 rounded-xl border text-left transition-all duration-200 flex flex-col justify-between ${
                  isMatch
                    ? 'border-primary bg-primary/5 shadow-xs ring-1 ring-primary'
                    : 'border-border/70 bg-card hover:bg-muted/40'
                }`}
              >
                <div>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 mb-1 font-normal">
                    {item.badge}
                  </Badge>
                  <h4 className="font-semibold text-xs leading-tight">{item.name}</h4>
                </div>
                <p className="text-[10px] text-muted-foreground line-clamp-2 mt-1">
                  {item.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Layout Principal: 3D Viewport + Panel Paramétrico */}
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Viewport 3D Three.js */}
        <div className="lg:col-span-7 flex flex-col gap-3">
          <Card className={`overflow-hidden border border-border/80 shadow-sm relative flex flex-col min-h-[520px] lg:min-h-[640px] transition-colors ${
            lightOn ? 'bg-[#181716]' : 'bg-[#F9F8F6]'
          }`}>
            {/* Barra flotante superior */}
            <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between pointer-events-none">
              <div className="pointer-events-auto flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={lightOn ? "default" : "secondary"}
                  className={`text-xs gap-1.5 shadow-sm h-8 ${
                    lightOn ? 'bg-amber-500 hover:bg-amber-600 text-black font-semibold' : ''
                  }`}
                  onClick={() => setLightOn(!lightOn)}
                >
                  <Lightbulb className={`h-3.5 w-3.5 ${lightOn ? 'text-black fill-black' : ''}`} />
                  {lightOn ? 'Luz ON' : 'Simular Luz'}
                </Button>

                <Button
                  size="sm"
                  variant={autoRotate ? "default" : "secondary"}
                  className={`text-xs gap-1.5 shadow-sm h-8 ${
                    autoRotate ? 'bg-primary text-primary-foreground' : ''
                  }`}
                  onClick={() => setAutoRotate(!autoRotate)}
                  title="Giro automático 360°"
                >
                  <RotateCw className={`h-3.5 w-3.5 ${autoRotate ? 'animate-spin' : ''}`} />
                  {autoRotate ? 'Giro ON' : 'Giro Auto'}
                </Button>

                <Button
                  size="sm"
                  variant="secondary"
                  className="text-xs gap-1.5 shadow-sm h-8"
                  onClick={handleRecenter}
                  title="Centrar y reajustar vista"
                >
                  <Crosshair className="h-3.5 w-3.5" /> Centrar
                </Button>

                <Button
                  size="sm"
                  variant={wireframe ? "default" : "secondary"}
                  className="text-xs gap-1 shadow-sm h-8"
                  onClick={() => setWireframe(!wireframe)}
                >
                  <Layers className="h-3.5 w-3.5" /> Malla
                </Button>
              </div>

              {/* Selector de Color de Filamento */}
              <div className="pointer-events-auto flex items-center gap-1.5 bg-card/90 backdrop-blur-xs p-1 rounded-lg border border-border/60 shadow-xs">
                {[
                  { hex: '#D8C7B5', name: 'Lino/Arena' },
                  { hex: '#F5F2EB', name: 'Blanco Cálido' },
                  { hex: '#B85D3B', name: 'Terracota' },
                  { hex: '#7A8B7B', name: 'Salvia' },
                  { hex: '#1C1C1E', name: 'Carbón Mate' }
                ].map(c => (
                  <button
                    key={c.hex}
                    title={c.name}
                    className={`w-5 h-5 rounded-full border transition-all ${
                      color === c.hex ? 'scale-110 ring-2 ring-primary ring-offset-1' : 'opacity-80 hover:opacity-100'
                    }`}
                    style={{ backgroundColor: c.hex }}
                    onClick={() => setColor(c.hex)}
                  />
                ))}
              </div>
            </div>

            {/* Canvas Three.js */}
            <div ref={mountRef} className="w-full flex-1 cursor-grab active:cursor-grabbing" />

            {/* Barra informativa inferior */}
            <div className={`p-3 backdrop-blur-xs border-t text-xs flex items-center justify-between ${
              lightOn ? 'bg-black/60 border-neutral-800 text-neutral-400' : 'bg-card/80 border-border/60 text-muted-foreground'
            }`}>
              <span className="flex items-center gap-1.5">
                <Box className="h-3.5 w-3.5 text-primary" />
                Rotar: Arrastre • Desplazar: Clic derecho • Zoom: Rueda
              </span>
              <div className="flex items-center gap-3">
                <span>Modo: <strong className="text-foreground">{params.printMode === 'vase' ? 'Vaso (1 Pared)' : `Sólido (${params.wallThickness}mm)`}</strong></span>
                <span>Encastre: <strong className="text-foreground">{params.hasSocketHole ? `Ø ${currentSocketDiam}mm (${params.socketType.toUpperCase()})` : 'Cerrado'}</strong></span>
              </div>
            </div>
          </Card>
        </div>

        {/* Panel de Controles Paramétricos */}
        <div className="lg:col-span-5 space-y-4">
          <Card className="border border-border/80 shadow-xs">
            <CardHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sliders className="h-4 w-4 text-primary" /> Motor Paramétrico
                </CardTitle>
                <Badge variant="secondary" className="text-[10px] font-mono">
                  {params.profile} • {params.texture}
                </Badge>
              </div>

              {/* Pestañas de Ajuste */}
              <div className="grid grid-cols-4 gap-1 pt-3">
                {[
                  { id: 'macro', label: 'Silueta', icon: Shapes },
                  { id: 'texture', label: 'Textura 3D', icon: Grid3X3 },
                  { id: 'lowpoly', label: 'Low-Poly', icon: Maximize2 },
                  { id: 'print', label: 'Impresión', icon: Printer }
                ].map(tab => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`py-1.5 px-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-xs'
                          : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <tab.icon className="h-3.5 w-3.5" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </CardHeader>

            <CardContent className="pt-4 space-y-4">
              {/* TAB 1: SILUETA MACRO */}
              {activeTab === 'macro' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs mb-1.5 block">Perfil de Silueta Macro</Label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { id: 'ripple', label: 'Ripple (Ondas)' },
                        { id: 'donut', label: 'Donut (Globo)' },
                        { id: 'accordion', label: 'Acordeón' },
                        { id: 'bottle', label: 'Botella Nórdica' },
                        { id: 'concave', label: 'Cóncavo' },
                        { id: 'linear', label: 'Lineal Cónico' }
                      ].map(p => (
                        <Button
                          key={p.id}
                          size="sm"
                          variant={params.profile === p.id ? "default" : "outline"}
                          className="text-xs h-8 px-2 justify-center"
                          onClick={() => setParams({ ...params, profile: p.id as MacroProfile })}
                        >
                          {p.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-border/50">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Altura Total (Z)</Label>
                        <span className="text-primary font-semibold">{params.height} mm</span>
                      </div>
                      <input
                        type="range"
                        min="70"
                        max="260"
                        value={params.height}
                        onChange={e => setParams({ ...params, height: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <Label>Ø Base Inferior</Label>
                          <span className="font-semibold">{params.bottomDiameter} mm</span>
                        </div>
                        <input
                          type="range"
                          min="70"
                          max="220"
                          value={params.bottomDiameter}
                          onChange={e => setParams({ ...params, bottomDiameter: Number(e.target.value) })}
                          className="w-full accent-primary cursor-pointer"
                        />
                      </div>

                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <Label>Ø Boca Superior</Label>
                          <span className="font-semibold">{params.topDiameter} mm</span>
                        </div>
                        <input
                          type="range"
                          min="45"
                          max="200"
                          value={params.topDiameter}
                          onChange={e => setParams({ ...params, topDiameter: Number(e.target.value) })}
                          className="w-full accent-primary cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-border/50">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Secciones Verticales (Ondas Macro)</Label>
                        <span className="text-primary font-semibold">{params.verticalSections} pliegues</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="6"
                        step="1"
                        value={params.verticalSections}
                        onChange={e => setParams({ ...params, verticalSections: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Amplitud del Pliegue Macro</Label>
                        <span className="text-primary font-semibold">{params.macroBulgeAmplitude} mm</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="25"
                        step="0.5"
                        value={params.macroBulgeAmplitude}
                        onChange={e => setParams({ ...params, macroBulgeAmplitude: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <Label>Lados de Sección Transversal (Polígono)</Label>
                        <span className="text-primary font-semibold">
                          {params.polygonSides} lados {params.polygonSides >= 48 ? '(Suave/Redondo)' : '(Facetado)'}
                        </span>
                      </div>
                      <input
                        type="range"
                        min="3"
                        max="64"
                        value={params.polygonSides}
                        onChange={e => setParams({ ...params, polygonSides: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: TEXTURA 3D REAL */}
              {activeTab === 'texture' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs mb-1.5 block">Micro-Estructura & Relieve Volumétrico</Label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { id: 'none', label: 'Ninguna (Lisa)' },
                        { id: 'knurling', label: 'Micro-Puntos (Knurling)' },
                        { id: 'diamond', label: 'Malla Diamantes' },
                        { id: 'triangles', label: 'Triángulos Relieve' },
                        { id: 'spiral_ribs', label: 'Costillas Espiral' },
                        { id: 'voronoi', label: 'Celular Orgánico' }
                      ].map(t => (
                        <Button
                          key={t.id}
                          size="sm"
                          variant={params.texture === t.id ? "default" : "outline"}
                          className="text-xs h-8 px-2 justify-start"
                          onClick={() => setParams({ ...params, texture: t.id as SurfaceTexture })}
                        >
                          {t.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {params.texture !== 'none' && (
                    <div className="space-y-3 pt-2 border-t border-border/50">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <Label>Profundidad de Relieve 3D</Label>
                          <span className="text-primary font-semibold">{params.textureDepth} mm</span>
                        </div>
                        <input
                          type="range"
                          min="0.5"
                          max="8.0"
                          step="0.2"
                          value={params.textureDepth}
                          onChange={e => setParams({ ...params, textureDepth: Number(e.target.value) })}
                          className="w-full accent-primary cursor-pointer"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <Label>Frecuencia Perimetral (X)</Label>
                            <span className="font-semibold">{params.textureScaleX}</span>
                          </div>
                          <input
                            type="range"
                            min="4"
                            max="60"
                            step="2"
                            value={params.textureScaleX}
                            onChange={e => setParams({ ...params, textureScaleX: Number(e.target.value) })}
                            className="w-full accent-primary cursor-pointer"
                          />
                        </div>

                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <Label>Frecuencia Vertical (Y)</Label>
                            <span className="font-semibold">{params.textureScaleY}</span>
                          </div>
                          <input
                            type="range"
                            min="2"
                            max="40"
                            step="1"
                            value={params.textureScaleY}
                            onChange={e => setParams({ ...params, textureScaleY: Number(e.target.value) })}
                            className="w-full accent-primary cursor-pointer"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 3: LOW-POLY & TORSIÓN */}
              {activeTab === 'lowpoly' && (
                <div className="space-y-4">
                  <div className="p-3 bg-muted/40 rounded-xl space-y-3 border border-border/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-semibold">Modo Low-Poly (Flat Shading)</Label>
                        <p className="text-[11px] text-muted-foreground">Normales afiladas por cara para aristas definidas</p>
                      </div>
                      <input
                        type="checkbox"
                        className="rounded h-4 w-4 cursor-pointer accent-primary"
                        checked={params.isLowPoly}
                        onChange={e => setParams({ ...params, isLowPoly: e.target.checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-border/40">
                      <div>
                        <Label className="text-xs font-semibold">Desfase Alternado (Staggered Grid)</Label>
                        <p className="text-[11px] text-muted-foreground">Genera el facetado de triángulos romboidales nórdicos</p>
                      </div>
                      <input
                        type="checkbox"
                        className="rounded h-4 w-4 cursor-pointer accent-primary"
                        checked={params.staggerVertices}
                        onChange={e => setParams({ ...params, staggerVertices: e.target.checked })}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <Label>Ángulo de Torsión Helicoidal (Twist)</Label>
                      <span className="text-primary font-semibold">{params.twistAngle}°</span>
                    </div>
                    <input
                      type="range"
                      min="-180"
                      max="180"
                      step="5"
                      value={params.twistAngle}
                      onChange={e => setParams({ ...params, twistAngle: Number(e.target.value) })}
                      className="w-full accent-primary cursor-pointer"
                    />
                  </div>
                </div>
              )}

              {/* TAB 4: MODO DE IMPRESIÓN & PORTALÁMPARAS */}
              {activeTab === 'print' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs mb-1.5 block">Modo de Generación de Malla Slicer</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant={params.printMode === 'vase' ? "default" : "outline"}
                        className="text-xs h-9 justify-center"
                        onClick={() => setParams({ ...params, printMode: 'vase' })}
                      >
                        Modo Vaso (1 Pared)
                      </Button>
                      <Button
                        size="sm"
                        variant={params.printMode === 'solid' ? "default" : "outline"}
                        className="text-xs h-9 justify-center"
                        onClick={() => setParams({ ...params, printMode: 'solid' })}
                      >
                        Modo Sólido (Con Espesor)
                      </Button>
                    </div>
                  </div>

                  {params.printMode === 'solid' && (
                    <div className="p-3 bg-muted/40 rounded-xl space-y-2 border border-border/50">
                      <div className="flex justify-between text-xs">
                        <Label>Espesor de Pared Interior</Label>
                        <span className="font-semibold text-primary">{params.wallThickness} mm</span>
                      </div>
                      <input
                        type="range"
                        min="0.8"
                        max="3.0"
                        step="0.2"
                        value={params.wallThickness}
                        onChange={e => setParams({ ...params, wallThickness: Number(e.target.value) })}
                        className="w-full accent-primary cursor-pointer"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Permite imprimir con relleno o múltiples perímetros (0.4 o 0.6mm nozzle).
                      </p>
                    </div>
                  )}

                  <div className="p-3 bg-muted/40 rounded-xl space-y-3 border border-border/50">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold">Incluir Encastre en la Base</Label>
                      <input
                        type="checkbox"
                        className="rounded h-4 w-4 cursor-pointer accent-primary"
                        checked={params.hasSocketHole}
                        onChange={e => setParams({ ...params, hasSocketHole: e.target.checked })}
                      />
                    </div>

                    {params.hasSocketHole && (
                      <div className="space-y-2 pt-2 border-t border-border/40">
                        <div className="flex gap-2">
                          {(['e27', 'e14', 'custom'] as SocketStandard[]).map(st => (
                            <Button
                              key={st}
                              size="sm"
                              variant={params.socketType === st ? "default" : "outline"}
                              className="text-xs h-7 flex-1"
                              onClick={() => setParams({ ...params, socketType: st })}
                            >
                              {st === 'e27' ? 'E27 (41.5mm)' : st === 'e14' ? 'E14 (28.5mm)' : 'Manual'}
                            </Button>
                          ))}
                        </div>

                        {params.socketType === 'custom' && (
                          <div className="flex items-center justify-between text-xs pt-1">
                            <span>Diámetro a medida:</span>
                            <Input
                              type="number"
                              value={params.customSocketDiameter}
                              onChange={e => setParams({ ...params, customSocketDiameter: Number(e.target.value) })}
                              className="w-24 h-7 text-xs"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Biblioteca de Presets Guardados en la Base de Datos */}
          <Card className="border border-border/80 shadow-xs">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold flex items-center gap-1.5 uppercase tracking-wider text-muted-foreground">
                <Bookmark className="h-3.5 w-3.5 text-primary" /> Guardar en Mi Biblioteca
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5">
              <div className="flex gap-2">
                <Input
                  placeholder="Ej. Lámpara Ondulada Mila 3.0"
                  value={presetName}
                  onChange={e => setPresetName(e.target.value)}
                  className="h-8 text-xs"
                />
                <Button size="sm" onClick={handleSavePreset} className="h-8 shrink-0 text-xs">
                  Guardar
                </Button>
              </div>

              {Array.isArray(presets) && presets.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border/50">
                  {presets.map(p => (
                    <Button
                      key={p.id}
                      variant="outline"
                      size="sm"
                      onClick={() => handleLoadUserPreset(p)}
                      className="text-xs h-6 px-2 rounded-full"
                    >
                      {p.name}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
